<script setup>
/**
 * CustomClassificationBadge
 *
 * Exibe uma lista de classificações personalizadas vindas de `comment.metadata.custom_classifications`
 *
 * Uso:
 * <CustomClassificationBadge :items="comment.metadata.custom_classifications" />
 *
 * Regras:
 * - Somente Tailwind (sem CSS customizado / inline styles)
 * - Composition API (<script setup>)
 * - PascalCase para o nome do componente
 * - Segue o padrão do MetadataBadge
 */
import { computed } from 'vue';

// Props
const props = defineProps({
  /**
   * Array de classificações personalizadas.
   * Ex: ['Fixação', 'Promoção', 'Engajamento']
   */
  items: {
    type: Array,
    required: true,
    validator(arr) {
      return Array.isArray(arr) && arr.every(i => typeof i === 'string');
    },
  },
  /**
   * Limite máximo de tags exibidas antes de mostrar "+N"
   */
  maxVisible: {
    type: Number,
    default: 4,
  },
});

/**
 * Paleta de cores determinística baseada no índice hash do texto.
 * Mantém consistência entre renders sem precisar armazenar estado.
 */
const colorPalette = [
  { bg: 'bg-[#F4EDFF]', text: 'text-[#874BE7]' }, // Roxo claro
  { bg: 'bg-[#D6FFD8]', text: 'text-[#0C8312]' }, // Verde
  { bg: 'bg-[#FFE7EB]', text: 'text-[#DF4C50]' }, // Vermelho claro
  { bg: 'bg-[#FAFFDC]', text: 'text-[#7B8D0A]' }, // Amarelo oliva
  { bg: 'bg-[#E6F4FF]', text: 'text-[#0F6CBD]' }, // Azul claro
  { bg: 'bg-[#F5F5F5]', text: 'text-[#60646C]' }, // Cinza neutro
];

function hashIndex(str) {
  return (
    str
      .split('')
      .reduce((acc, ch) => (acc + ch.charCodeAt(0) * 17) % 104729, 0) %
    colorPalette.length
  );
}

const normalizedItems = computed(() =>
  (props.items || []).map(t => (t || '').trim()).filter(t => t.length > 0)
);

const visibleItems = computed(() =>
  normalizedItems.value.slice(0, props.maxVisible)
);

const hiddenCount = computed(() =>
  Math.max(0, normalizedItems.value.length - props.maxVisible)
);

const hasHidden = computed(() => hiddenCount.value > 0);
</script>

<template>
  <div
    v-if="normalizedItems.length"
    class="flex items-center gap-2"
    data-component="custom-classification-badge"
  >
    <div
      v-for="(item, idx) in visibleItems"
      :key="item + idx"
      :class="`flex items-center gap-2 rounded-lg p-2 text-xs font-semibold ${colorPalette[hashIndex(item)].bg} ${colorPalette[hashIndex(item)].text}`"
    >
      <span class="flex-shrink-0 truncate max-w-[120px]" :title="item">{{
        item
      }}</span>
    </div>
    <div
      v-if="hasHidden"
      class="flex items-center gap-2 rounded-lg p-2 text-xs font-semibold bg-[#F5F5F5] text-[#60646C]"
      :aria-label="`+${hiddenCount} mais`"
      :title="`+${hiddenCount} mais`"
    >
      <span class="flex-shrink-0">{{ '+' + hiddenCount }}</span>
    </div>
  </div>
  <div v-else />
</template>
